import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useGlobalContext } from '../../../../../utils/Context'
import { getInvoices } from '../../../../../utils/WeightDiscrepancyApi'
import FormLoader from '../../../../../components/loader/FormLoader'
import Pagination from '../../../../../components/pagination/Pagination'

interface InvoiceListProps {
  onSelectInvoice: (id: string) => void
}

function InvoiceList({ onSelectInvoice }: InvoiceListProps) {
  const { notify } = useGlobalContext()
  const [invoices, setInvoices] = useState<any>({
    invoices: [],
    pagination: {},
  })
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')

  function fetchInvoices(status: string, page: number, limit: number) {
    setLoading(true)
    getInvoices(status, page, limit)
      .then((result: any) => {
        if (result.status === 200) {
          setInvoices(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchInvoices(statusFilter, 1, 10)
  }, [])

  const handleStatusChange = (e: any) => {
    const s = e.target.value
    setStatusFilter(s)
    fetchInvoices(s, 1, 10)
  }

  const badgeClass = (status: string) =>
    status === 'sent' ? 'pending' : status

  const badgeLabel = (status: string) =>
    status === 'sent'
      ? 'Pending'
      : status.charAt(0).toUpperCase() + status.slice(1)

  const remaining = (item: any) =>
    item.remainingAmount ?? item.totalAmount - item.amountPaid

  return (
    <div>
      {/* Filter */}
      <div className='wd-filter-bar'>
        <Icon icon='mdi:filter-variant' color='#9ca3af' width='18' />
        <select value={statusFilter} onChange={handleStatusChange}>
          <option value=''>All</option>
          <option value='sent'>Pending (Sent)</option>
          <option value='overdue'>Overdue</option>
          <option value='paid'>Paid</option>
        </select>
      </div>

      {loading ? (
        <FormLoader position='center' />
      ) : invoices.invoices.length > 0 ? (
        <>
          {/* ── Mobile cards ─────────────────────────────────────────── */}
          <div className='wd-mobile-cards'>
            {invoices.invoices.map((item: any) => (
              <div className='wd-mobile-card' key={item._id}>
                <div className='wd-mc-top'>
                  {/* <span className='wd-mc-id'>{item.invoiceNumber}</span> */}
                  <span className={`wd-badge ${badgeClass(item.status)}`}>
                    {badgeLabel(item.status)}
                  </span>
                </div>

                <div className='wd-mc-row'>
                  <h4>Date Sent</h4>
                  <p>{new Date(item.sentAt).toLocaleDateString()}</p>
                </div>
                <div className='wd-mc-row'>
                  <h4>Deadline</h4>
                  <p className={item.status === 'overdue' ? 'wd-td-error' : ''}>
                    {new Date(item.deadline).toLocaleDateString()}
                  </p>
                </div>
                <div className='wd-mc-row'>
                  <h4>Total</h4>
                  <p>
                    <strong>₦{item.totalAmount.toLocaleString()}</strong>
                  </p>
                </div>
                <div className='wd-mc-row'>
                  <h4>Remaining</h4>
                  <p
                    className={
                      remaining(item) > 0 ? 'wd-td-error' : 'wd-td-success'
                    }
                  >
                    ₦{remaining(item).toLocaleString()}
                  </p>
                </div>

                <div className='wd-mc-action'>
                  <button onClick={() => onSelectInvoice(item._id)}>
                    <Icon icon='mdi:eye-outline' width='16' />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ── Desktop table ─────────────────────────────────────────── */}
          <div className='wd-desktop-table'>
            <div className='wd-table-card'>
              <table className='wd-table'>
                <thead>
                  <tr>
                    {[
                      '#',
                      'Date Sent',
                      'Deadline',
                      'Total',
                      'Remaining',
                      'Status',
                      '',
                    ].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.invoices.map((item: any, i: number) => (
                    <tr key={item._id}>
                      <td className='wd-td-num'>{i + 1}</td>
                      <td>{new Date(item.sentAt).toLocaleDateString()}</td>
                      <td
                        className={
                          item.status === 'overdue' ? 'wd-td-error' : ''
                        }
                      >
                        {new Date(item.deadline).toLocaleDateString()}
                      </td>
                      <td className='wd-td-bold'>
                        ₦{item.totalAmount.toLocaleString()}
                      </td>
                      <td
                        className={
                          remaining(item) > 0 ? 'wd-td-error' : 'wd-td-success'
                        }
                      >
                        ₦{remaining(item).toLocaleString()}
                      </td>
                      <td>
                        <span className={`wd-badge ${badgeClass(item.status)}`}>
                          {badgeLabel(item.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          className='wd-view-btn'
                          onClick={() => onSelectInvoice(item._id)}
                        >
                          <Icon icon='mdi:eye-outline' width='15' />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={invoices.pagination.page}
            totalCount={invoices.pagination.pages}
            pageSize={invoices.pagination.limit}
            onPageChange={(page: number, pageSize: number) =>
              fetchInvoices(statusFilter, page, pageSize)
            }
          />
        </>
      ) : (
        <div className='empty flex justify-center'>
          <div className='empty-cover'>
            <Icon icon='mdi:file-document-remove-outline' />
            <h1>No Invoices To Show</h1>
            <p>No invoices match the selected filter.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default InvoiceList
